import { acquireAnnotationMutex, releaseAnnotationMutex } from "admin/rest_api";
import { Button } from "antd";
import Toast from "libs/toast";
import messages from "messages";
import {
  call,
  cancel,
  delay,
  type FixedTask,
  fork,
  put,
  retry,
  take,
  takeEvery,
} from "typed-redux-saga";
import { isAnnotationEditableByNonOwners } from "viewer/model/accessors/annotation_accessor";
import {
  type SetCollaborationModeAction,
  setIsUpdatingAnnotationCurrentlyAllowedAction,
} from "viewer/model/actions/annotation_actions";
import {
  type SetIsMutexAcquiredAction,
  type SubscribeToAnnotationMutexAction,
  setIsMutexAcquiredAction,
  setUserHoldingMutexAction,
  subscribeToAnnotationMutexAction,
  type UnsubscribeFromAnnotationMutexAction,
  unsubscribeFromAnnotationMutexAction,
} from "viewer/model/actions/save_actions";
import type { Saga } from "viewer/model/sagas/effect_generators";
import { select } from "viewer/model/sagas/effect_generators";
import { ensureWkInitialized } from "../ready_sagas";

// Also refer to application.conf where annotation.mutex.expiryTime is defined
// (typically, 2 minutes).

const MUTEX_NOT_ACQUIRED_KEY = "MutexCouldNotBeAcquired";
const MUTEX_ACQUIRED_KEY = "AnnotationMutexAcquired";
const ACQUIRE_MUTEX_INTERVAL = import.meta.env.MODE === "test" ? 1 * 1000 : 60 * 1000;
const DELAY_AFTER_FAILED_MUTEX_FETCH = import.meta.env.MODE === "test" ? 1 * 1000 : 10 * 1000;
const RETRY_COUNT = 20; // 12 retries with 60/12=5 seconds backup delay
const INITIAL_BACKOFF_TIME = 750;
const BACKOFF_TIME_MULTIPLIER = 1.2;
const BACKOFF_JITTER_LOWER_PERCENT = 0.0;
const MAX_AD_HOC_RETRY_TIME = 10 * 1000;
const BACKOFF_JITTER_UPPER_PERCENT = 0.15;
const MAX_RELEASE_RETRY_INTERVAL = 30 * 1000;

export enum MutexFetchingStrategy {
  AdHoc = "AdHoc",
  Continuously = "Continuously",
}

type MutexLogicState = {
  isInitialRequest: boolean;
  fetchingStrategy: MutexFetchingStrategy;
  // runningContinuousMutexAcquiringSaga is only non-null in collaborationMode=Exclusive.
  runningContinuousMutexAcquiringSaga: FixedTask<void> | null;
  // runningAdHocMutexAcquiringSaga is only non-null in collaborationMode=Concurrent when
  // a mutex is currently needed.
  runningAdHocMutexAcquiringSaga: FixedTask<void> | null;
  subscribersToMutex: Record<number, string>; // Mapping from self maintained id to caller id.
};

function* getDoesHaveMutex(): Saga<boolean> {
  return yield* select((state) => state.save.mutexState.hasAnnotationMutex);
}

export function* getCurrentMutexFetchingStrategy(): Saga<MutexFetchingStrategy> {
  let fetchingStrategy = MutexFetchingStrategy.Continuously;
  const collaborationMode = yield* select((state) => state.annotation.collaborationMode);
  if (collaborationMode === "Concurrent") {
    // The active annotation is currently in proofreading state. Thus, having the mutex only on save demand works in regards to the current milestone.
    fetchingStrategy = MutexFetchingStrategy.AdHoc;
  }
  return fetchingStrategy;
}

function* determineInitialMutexLogicState(): Saga<MutexLogicState> {
  const fetchingStrategy = yield* call(getCurrentMutexFetchingStrategy);

  const mutexLogicState: MutexLogicState = {
    isInitialRequest: true,
    fetchingStrategy,
    runningContinuousMutexAcquiringSaga: null,
    runningAdHocMutexAcquiringSaga: null,
    subscribersToMutex: {},
  };
  return mutexLogicState;
}

// Should only be accessed via getMutexLogicState().
let mutexLogicState: MutexLogicState | null = null;

// Exported to be used in tests.
export function getMutexLogicState(): MutexLogicState {
  if (!mutexLogicState) {
    throw new Error("Mutex state not yet loaded!");
  }
  return mutexLogicState;
}

function setMutexLogicSate(state: MutexLogicState): void {
  mutexLogicState = state;
}

export function* acquireAnnotationMutexMaybe(): Saga<void> {
  /*
   * This saga is directly called by the root saga.
   *
   */
  yield* call(ensureWkInitialized);
  // If the user is potentially allowed to update the annotation, start mutex fetching procedure.
  const allowUpdate = yield* select((state) => state.annotation.restrictions.allowUpdate);
  if (!allowUpdate) {
    // We are in an read-only annotation. There's no point in acquiring mutexes.
    return;
  }

  const mutexLogicState = yield* call(determineInitialMutexLogicState);
  setMutexLogicSate(mutexLogicState);

  yield* fork(watchMutexStateChangesForNotification, mutexLogicState);
  yield* fork(watchForCollaborationModeChange, mutexLogicState);
  yield* fork(watchForMutexSubscriptionActions, mutexLogicState);
  yield* takeEvery(["SUBSCRIBE_TO_ANNOTATION_MUTEX"], autoTimeoutSubscription);

  const othersMayEdit = yield* select((state) => isAnnotationEditableByNonOwners(state.annotation));
  if (othersMayEdit) {
    // Only start initial acquiring of mutex if othersMayEdit is true.
    yield* call(restartMutexAcquiringSaga, mutexLogicState);
  }
}

function getUnsubscribeFromAnnotationMutexSaga(
  id: number,
  warnIfAlreadyUnsubscribed: boolean = true,
): () => Saga<void> {
  function* unsubscribe(): Saga<void> {
    const state = getMutexLogicState();
    const callerId = state.subscribersToMutex[id];
    if (!callerId) {
      if (warnIfAlreadyUnsubscribed) {
        console.warn(
          `Tried to unsubscribe from annotation mutex with id ${id} but it was not found. Maybe the unsubscribe was called multiple times.`,
        );
      }
      return;
    }
    delete state.subscribersToMutex[id];
    yield* put(unsubscribeFromAnnotationMutexAction(id));
    if (!state.runningAdHocMutexAcquiringSaga) {
      console.warn(
        "Unsubscribing from annotation mutex, while no saga is running that takes care of acquiring the annotation mutex. Unsubscriber id:",
        callerId,
      );
    }
    const hasNoSubscribersLeft = Object.keys(state.subscribersToMutex).length === 0;
    if (hasNoSubscribersLeft && state.runningAdHocMutexAcquiringSaga) {
      yield* cancel(state.runningAdHocMutexAcquiringSaga);
      state.runningAdHocMutexAcquiringSaga = null;
      yield* call(releaseMutex);
    }
  }
  return unsubscribe;
}

const MUTEX_SUBSCRIPTION_TIMEOUT = 5 * 60 * 1000;
function* autoTimeoutSubscription(action: SubscribeToAnnotationMutexAction): Saga<void> {
  yield delay(MUTEX_SUBSCRIPTION_TIMEOUT);
  const warnIfAlreadyUnsubscribed = false;
  yield call(
    getUnsubscribeFromAnnotationMutexSaga(action.subscriptionId, warnIfAlreadyUnsubscribed),
  );
}

export function* subscribeToAnnotationMutex(callerId: string): Saga<() => Saga<void>> {
  /*
   * Blocks until the mutex annotation has been acquired and returns a function
   * which should be used to release the annotation mutex (note, that the mutex
   * will only be released when no other "subscription" is pending).
   * Note: There is default timeout on a subscription after which it will be invalid.
   * The idea is in case an operation is stuck to not block other users infinitely.
   */
  const state = getMutexLogicState();
  let newId = Math.round(Math.random() * 10000);
  while (newId in state.subscribersToMutex) {
    newId = Math.round(Math.random() * 10000);
  }
  state.subscribersToMutex[newId] = callerId;
  yield* put(subscribeToAnnotationMutexAction(newId, callerId));

  while (true) {
    const doesHaveMutex = yield* call(getDoesHaveMutex);
    const othersMayEdit = yield* select((state) =>
      isAnnotationEditableByNonOwners(state.annotation),
    );
    if (doesHaveMutex || !othersMayEdit) {
      return getUnsubscribeFromAnnotationMutexSaga(newId);
    }
    yield* take("SET_IS_MUTEX_ACQUIRED");
  }
}

// Needed for tests
export function* clearAllSubscriptions() {
  const state = getMutexLogicState();
  for (const numId of Object.keys(state.subscribersToMutex)) {
    const unsubscribe = getUnsubscribeFromAnnotationMutexSaga(Number(numId));
    yield* call(unsubscribe);
  }
}

function* restartMutexAcquiringSaga(mutexLogicState: MutexLogicState): Saga<void> {
  const newFetchingStrategy = yield* call(getCurrentMutexFetchingStrategy);
  const oldFetchingStrategy = mutexLogicState.fetchingStrategy;
  const didStrategyChange = newFetchingStrategy !== oldFetchingStrategy;
  if (didStrategyChange && mutexLogicState.runningContinuousMutexAcquiringSaga != null) {
    yield* cancel(mutexLogicState.runningContinuousMutexAcquiringSaga);
    mutexLogicState.runningContinuousMutexAcquiringSaga = null;
  } else if (didStrategyChange && mutexLogicState.runningAdHocMutexAcquiringSaga != null) {
    yield* cancel(mutexLogicState.runningAdHocMutexAcquiringSaga);
    mutexLogicState.runningAdHocMutexAcquiringSaga = null;
  }
  const othersMayEdit = yield* select((state) => isAnnotationEditableByNonOwners(state.annotation));
  if (!othersMayEdit) {
    return;
  }
  const hasNoRunningMutexAcquiringSaga =
    mutexLogicState.runningContinuousMutexAcquiringSaga == null &&
    mutexLogicState.runningAdHocMutexAcquiringSaga == null;
  if (didStrategyChange || hasNoRunningMutexAcquiringSaga) {
    mutexLogicState.fetchingStrategy = newFetchingStrategy;
    yield* call(startSagaWithAppropriateMutexFetchingStrategy, mutexLogicState);
  }
}

function* startSagaWithAppropriateMutexFetchingStrategy(
  mutexLogicState: MutexLogicState,
): Saga<void> {
  if (mutexLogicState.fetchingStrategy === MutexFetchingStrategy.AdHoc) {
    const hasActiveMutexSubscribers = Object.keys(mutexLogicState.subscribersToMutex).length > 0;
    // While the fetching strategy is ad hoc, updating should be allowed.
    yield* put(setIsUpdatingAnnotationCurrentlyAllowedAction(true));
    if (hasActiveMutexSubscribers) {
      const taskPermanentlyAcquiringMutex = yield* fork(tryAcquireMutexForSaving, mutexLogicState);
      mutexLogicState.runningAdHocMutexAcquiringSaga = taskPermanentlyAcquiringMutex;
    } else {
      // If we still have the mutex, release it first as strategy is now ad-hoc.
      const currentlyHavingMutex = yield* select(
        (state) => state.save.mutexState.hasAnnotationMutex,
      );
      if (currentlyHavingMutex) {
        yield* call(releaseMutex);
      }
    }
  } else {
    const continuousSaga = yield* fork(tryAcquireMutexContinuously, mutexLogicState);
    mutexLogicState.runningContinuousMutexAcquiringSaga = continuousSaga;
  }
}

function* tryAcquireMutexContinuously(mutexLogicState: MutexLogicState): Saga<never> {
  /*
   * Try to acquire mutex indefinitely (saga can be cancelled from the outside with cancel or
   * race).
   */
  const annotationId = yield* select((storeState) => storeState.annotation.annotationId);
  const activeUser = yield* select((state) => state.activeUser);
  mutexLogicState.isInitialRequest = true;

  // We can simply use an infinite loop here, because the saga will be cancelled by
  // reactToOthersMayEditChanges when collaborationMode changes.
  while (true) {
    const blockedByUser = yield* select((state) => state.save.mutexState.blockedByUser);
    if (blockedByUser == null || blockedByUser.id !== activeUser?.id) {
      // If the annotation is currently not blocked by the active user,
      // we immediately disallow updating the annotation.
      yield* put(setIsUpdatingAnnotationCurrentlyAllowedAction(false));
    }
    try {
      const { canEdit, blockedByUser: blockedByUser } = yield* retry(
        RETRY_COUNT,
        ACQUIRE_MUTEX_INTERVAL / RETRY_COUNT,
        acquireAnnotationMutex,
        annotationId,
      );
      yield* put(setIsUpdatingAnnotationCurrentlyAllowedAction(canEdit));
      yield* put(setUserHoldingMutexAction(blockedByUser));
      yield* put(setIsMutexAcquiredAction(canEdit));

      if (canEdit !== (yield* call(getDoesHaveMutex))) {
        // Only dispatch the action if it changes the store to avoid
        // unnecessary notifications.
        yield* put(setIsMutexAcquiredAction(canEdit));
      }
    } catch (error) {
      // No need to check whether the saga was cancelled.
      // A cancelled saga does not reach the catch block but the finally block.
      // The error is printed before the first yield here, since it would be
      // swallowed in the test contexts otherwise (making debugging harder).
      console.error("Error while trying to acquire mutex.", error);
      yield* put(setUserHoldingMutexAction(undefined));
      yield* put(setIsUpdatingAnnotationCurrentlyAllowedAction(false));
      if (yield* call(getDoesHaveMutex)) {
        yield* put(setIsMutexAcquiredAction(false));
      }
    }
    mutexLogicState.isInitialRequest = false;
    yield* call(delay, ACQUIRE_MUTEX_INTERVAL);
  }
}

function* acquireMutexInitiallyForAdHocStrategy(annotationId: string): Saga<void> {
  let backoffTime = INITIAL_BACKOFF_TIME;
  const startingTime = new Date().getMilliseconds();
  let canEdit = false;
  let blockedByUser = null;
  let showingToast = false;

  // We can simply use an infinite loop here, because the saga will be cancelled by
  // reactToOthersMayEditChanges when collaborationMode changes.
  while (true) {
    try {
      const mutexResult = yield* call(acquireAnnotationMutex, annotationId);
      canEdit = mutexResult.canEdit;
      blockedByUser = mutexResult.blockedByUser;

      yield* put(setUserHoldingMutexAction(blockedByUser));
      yield* put(setIsMutexAcquiredAction(canEdit));
      if (canEdit) {
        return;
      }
    } catch (error) {
      if (import.meta.env.MODE === "test") {
        // In unit tests, that explicitly control this generator function,
        // the console.error after the next yield won't be printed, because
        // test assertions on the yield will already throw.
        // Therefore, we also print the error in the test context.
        console.error("Error while trying to acquire mutex:", error);
      }
      console.error("Error while trying to acquire mutex.", error);
    }
    if (!showingToast && new Date().getMilliseconds() - startingTime > MAX_AD_HOC_RETRY_TIME) {
      const blockingUserName = blockedByUser
        ? `${blockedByUser.firstName} ${blockedByUser.lastName}`
        : "unknown";
      Toast.warning(
        `Could not get the annotations write-lock for more than ${MAX_AD_HOC_RETRY_TIME / 1000} seconds.
        User ${blockingUserName} is currently blocking the annotation.
        This might be due to using non-live collab supported features.
        Ensure they are sticking to tools supporting live collaboration.`,
        { sticky: true },
      );
      showingToast = true;
    }

    const backOffJitter =
      Math.random() * (BACKOFF_JITTER_UPPER_PERCENT - BACKOFF_JITTER_LOWER_PERCENT) +
      BACKOFF_JITTER_LOWER_PERCENT;
    backoffTime = Math.min(
      backoffTime * BACKOFF_TIME_MULTIPLIER + backoffTime * backOffJitter,
      MAX_AD_HOC_RETRY_TIME,
    );
    yield* call(delay, backoffTime);
  }
}

function* keepAnnotationMutexForAdHocStrategy(annotationId: string): Saga<void> {
  // We got the mutex once, now keep it until this saga is cancelled due to saving finished.
  let canEdit = true;
  let blockedByUser = null;
  // Wait a little since we already just acquired the mutex in acquireMutexInitiallyForAdHocStrategy.
  yield* call(delay, ACQUIRE_MUTEX_INTERVAL);
  while (true) {
    try {
      const mutexInfo = yield* call(acquireAnnotationMutex, annotationId);
      canEdit = mutexInfo.canEdit;
      blockedByUser = mutexInfo.blockedByUser;
      yield* put(setUserHoldingMutexAction(blockedByUser));
      yield* put(setIsMutexAcquiredAction(canEdit));
      if (canEdit) {
        // Only wait for next refetching of the mutex in case the user can edit.
        // Else directly go to the error case outside the try-catch block below.
        yield* call(delay, ACQUIRE_MUTEX_INTERVAL);
      }
    } catch (error) {
      console.error("Failed to continuously acquire mutex.", error);
      yield* put(setUserHoldingMutexAction(undefined));
      yield* put(setIsMutexAcquiredAction(false));
      Toast.warning(
        "Unable to get write-lock needed to update the annotation. Please check your connection to WEBKNOSSOS. See the console for more information. Retrying soon.",
      );
      yield* call(delay, DELAY_AFTER_FAILED_MUTEX_FETCH);
    }
    // This needs to be outside of the try block to make sure this saga crashes as intended.
    if (!canEdit) {
      // If this code is reached, the user once already had the mutex, but re-acquiring was needed as saving took quite long.
      // In case of a network error, the catch block should take care of re-trying to acquire the mutex.
      // But if the server replies that the current user cannot edit at the moment, the previously acquired mutex must have
      // expanded and another user must have it at the moment. This means that there a likely saving and version conflicts now as
      // this user lost the mutex while still in the process of syncing with the backend. Thus, throwing an error to show a toast and crashing the saga
      // leads the user to having to reload wk to minimize lost work, instead of allowing to continue to edit data.
      throw new Error(
        `No longer owner of the annotation mutex. Instead user ${blockedByUser ? `${blockedByUser.firstName} ${blockedByUser?.lastName} (${blockedByUser?.id})` : "unknown user"} has the mutex.`,
      );
    }
  }
}

function* tryAcquireMutexForSaving(mutexLogicState: MutexLogicState): Saga<void> {
  /*
   * Try to acquire mutex indefinitely (saga can be cancelled from the outside with cancel or
   * race).
   */
  const annotationId = yield* select((storeState) => storeState.annotation.annotationId);
  mutexLogicState.isInitialRequest = true; // Never show popup about the mutex now being acquired.

  yield* call(acquireMutexInitiallyForAdHocStrategy, annotationId);
  yield* call(keepAnnotationMutexForAdHocStrategy, annotationId);
}

function* watchForCollaborationModeChange(mutexLogicState: MutexLogicState): Saga<void> {
  function* onChange({ collaborationMode }: SetCollaborationModeAction): Saga<void> {
    if (collaborationMode !== "OwnerOnly") {
      yield* call(restartMutexAcquiringSaga, mutexLogicState);
    } else {
      // Collaboration was turned off by the activeUser. Cancel any running mutex
      // acquisition saga and release the mutex if currently held.
      if (mutexLogicState.runningContinuousMutexAcquiringSaga != null) {
        yield* cancel(mutexLogicState.runningContinuousMutexAcquiringSaga);
        mutexLogicState.runningContinuousMutexAcquiringSaga = null;
      }
      if (mutexLogicState.runningAdHocMutexAcquiringSaga != null) {
        yield* cancel(mutexLogicState.runningAdHocMutexAcquiringSaga);
        mutexLogicState.runningAdHocMutexAcquiringSaga = null;
      }
      const stillHasAnnotationMutex = yield* select(
        (state) => state.save.mutexState.hasAnnotationMutex,
      );
      if (stillHasAnnotationMutex) {
        yield* call(releaseMutex);
      }
      // Since only the owner can turn off collaboration, they should be able to edit, too.
      // Still, let's check that owner === activeUser to be extra safe.
      const owner = yield* select((storeState) => storeState.annotation.owner);
      const activeUser = yield* select((state) => state.activeUser);
      const isAnnotationOwner = (activeUser && owner?.id === activeUser?.id) || false;
      yield* put(setIsUpdatingAnnotationCurrentlyAllowedAction(isAnnotationOwner));
    }
  }
  yield* takeEvery("SET_COLLABORATION_MODE", onChange);
}

function* watchForMutexSubscriptionActions(mutexLogicState: MutexLogicState): Saga<void> {
  // If a subscription is added or removed we may need to start/stop the ad-hoc mutex fetching.
  function* reactToSubscriptionChange(
    action: SubscribeToAnnotationMutexAction | UnsubscribeFromAnnotationMutexAction,
  ): Saga<void> {
    const othersMayEdit = yield* select((state) =>
      isAnnotationEditableByNonOwners(state.annotation),
    );
    if (!othersMayEdit) {
      return;
    }
    if (action.type === "SUBSCRIBE_TO_ANNOTATION_MUTEX") {
      // Start the ad hoc mutex acquisition if needed.
      yield* call(restartMutexAcquiringSaga, mutexLogicState);
    } else {
      const subscriptionCount = Object.keys(mutexLogicState.subscribersToMutex).length;
      // Stop the mutex acquisition if needed.
      if (subscriptionCount === 0) {
        if (mutexLogicState.runningContinuousMutexAcquiringSaga != null) {
          yield* cancel(mutexLogicState.runningContinuousMutexAcquiringSaga);
          mutexLogicState.runningContinuousMutexAcquiringSaga = null;
        }
        const stillHasAnnotationMutex = yield* select(
          (state) => state.save.mutexState.hasAnnotationMutex,
        );
        if (stillHasAnnotationMutex) {
          yield* call(releaseMutex);
        }
      }
    }
  }

  yield* takeEvery(
    ["SUBSCRIBE_TO_ANNOTATION_MUTEX", "UNSUBSCRIBE_FROM_ANNOTATION_MUTEX"],
    reactToSubscriptionChange,
  );
}

function* watchMutexStateChangesForNotification(mutexLogicState: MutexLogicState): Saga<void> {
  let wasMutexAlreadyAcquiredBefore = yield* select(
    (state) => state.save.mutexState.hasAnnotationMutex,
  );
  yield* takeEvery(
    "SET_IS_MUTEX_ACQUIRED",
    function* ({ isMutexAcquired }: SetIsMutexAcquiredAction) {
      const othersMayEdit = yield* select((state) =>
        isAnnotationEditableByNonOwners(state.annotation),
      );
      if (!othersMayEdit) {
        return;
      }
      if (mutexLogicState.fetchingStrategy === MutexFetchingStrategy.AdHoc) {
        return;
      }
      if (isMutexAcquired) {
        Toast.close(MUTEX_NOT_ACQUIRED_KEY);
        if (!mutexLogicState.isInitialRequest && !wasMutexAlreadyAcquiredBefore) {
          const message = (
            <>
              {messages["annotation.acquiringMutexSucceeded"]}
              <Button onClick={() => location.reload()}>Reload the annotation</Button>
            </>
          );
          Toast.success(message, { sticky: true, key: MUTEX_ACQUIRED_KEY });
        }
      } else {
        Toast.close(MUTEX_ACQUIRED_KEY);
        const blockedByUser = yield* select((state) => state.save.mutexState.blockedByUser);
        const message =
          blockedByUser != null
            ? messages["annotation.acquiringMutexFailed"]({
                userName: `${blockedByUser.firstName} ${blockedByUser.lastName}`,
              })
            : messages["annotation.acquiringMutexFailed.noUser"];
        Toast.warning(message, { sticky: true, key: MUTEX_NOT_ACQUIRED_KEY });
      }
      wasMutexAlreadyAcquiredBefore = yield* select(
        (state) => state.save.mutexState.hasAnnotationMutex,
      );
      mutexLogicState.isInitialRequest = false;
    },
  );
}

function* releaseMutex() {
  const annotationId = yield* select((storeState) => storeState.annotation.annotationId);
  let successfullyReleaseMutex = false;
  let backoffTime = 1000;
  // Enforce released mutex even when initial request failed due to e.g. network error.
  // In case another user got the mutex in meantime, releasing this users mutex still yield a successful request.
  while (!successfullyReleaseMutex) {
    try {
      yield call(releaseAnnotationMutex, annotationId);
      successfullyReleaseMutex = true;
    } catch (error) {
      console.error("Could not release mutex", error);
      yield delay(backoffTime);
      backoffTime = Math.min(backoffTime ** 2, MAX_RELEASE_RETRY_INTERVAL);
    }
  }
  yield* put(setUserHoldingMutexAction(null));
  yield* put(setIsMutexAcquiredAction(false));
}
